import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

const STATUS_VALUES = ['new', 'in_progress', 'done'];

function AdminSiteContactMessagesPage() {
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [search, setSearch] = React.useState('');

  const fetchUsers = async () => {
    try {
      const res = await pb.collection('users').getList(1, 200, { sort: 'name' });
      setUsers(res?.items ?? []);
    } catch {
      setUsers([]);
    }
  };

  const fetchAll = async () => {
    setError('');
    try {
      const res = await pb.collection('contact_messages').getList(1, 200, { sort: '-created', expand: 'handled_by' });
      setItems(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des messages');
      setItems([]);
    }
  };

  React.useEffect(() => {
    fetchUsers();
    fetchAll();
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => clearTimeout(t);
  }, []);

  const updateMessage = async (id, patch) => {
    setLoading(true);
    try {
      await pb.collection('contact_messages').update(id, patch);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur de mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    setLoading(true);
    try {
      await pb.collection('contact_messages').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(m => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const blob = [m.full_name, m.email, m.phone, m.subject, m.message, m.status]
      .map(v => String(v || '').toLowerCase())
      .join(' ');
    return blob.includes(q);
  });

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginTop: 0 }}>Contact · Messages</h1>
          <p style={{ marginTop: 6 }}>Boîte de réception. Vous pouvez changer le statut et assigner un membre.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <div style={{ position: 'relative' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              style={{ width: 260, background: 'var(--admin-panel)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', borderRadius: 10, padding: '8px 10px', outline: 'none' }}
            />
          </div>
          <button className="header-pill" onClick={fetchAll} disabled={loading}>Rafraîchir</button>
        </div>
      </div>

      <style>{`
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .badge{ padding:4px 10px; border-radius:999px; border:1px solid var(--admin-border); font-size:.82rem; }
        select, textarea{ background: var(--admin-panel-2); border:1px solid var(--admin-border); color: var(--admin-text); border-radius: 10px; padding: 8px 10px; outline:none; }
        select:focus, textarea:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        {filtered.map((m) => (
          <div key={m.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 240, flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{m.subject || 'Sans sujet'}</div>
                <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', marginTop: 4 }}>
                  {m.full_name || '—'} · {m.email || '—'}{m.phone ? ` · ${m.phone}` : ''}
                </div>
              </div>
              <span className="badge">{new Date(m.created).toLocaleString()}</span>
            </div>

            {m.message && (
              <div style={{ marginTop: 10 }}>
                <textarea value={m.message} readOnly style={{ width: '100%', minHeight: 120 }} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: '.85rem', color: 'var(--admin-muted)' }}>Statut</div>
                <select value={m.status || 'new'} onChange={(e) => updateMessage(m.id, { status: e.target.value })} disabled={loading}>
                  {STATUS_VALUES.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: '.85rem', color: 'var(--admin-muted)' }}>Assigné à</div>
                <select
                  value={m.handled_by || ''}
                  onChange={(e) => updateMessage(m.id, { handled_by: e.target.value || null })}
                  disabled={loading}
                >
                  <option value="">—</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button className="header-pill" onClick={() => {
                  const body = [
                    `Nom: ${m.full_name || ''}`,
                    `Email: ${m.email || ''}`,
                    `Téléphone: ${m.phone || ''}`,
                    `Sujet: ${m.subject || ''}`,
                    '',
                    m.message || ''
                  ].join('\n');
                  try { navigator.clipboard.writeText(body); alert('Message copié'); } catch {}
                }}>Copier</button>
                <button className="header-pill" onClick={() => handleDelete(m.id)} disabled={loading}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && allowEmpty && (
          <div style={{ padding: 16, color: 'var(--admin-muted)' }}>Aucun message.</div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminSiteContactMessagesPage;
